import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  Paper,
  TableContainer,
  makeStyles,
  Typography,
  CircularProgress,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import StyledButton from "../../../../common/models/Button";
import { appUrl } from "../../../../../utils/config";
import axios from "axios";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "650px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  resetChangesButton: {
    border: "1px solid #000000",
    color: "#000000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    marginTop: theme.spacing(2),
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: {
    color: theme.palette.error.main,
  },
  switchBase: {
    "&.Mui-checked": {
      color: "#007bff",
    },
    "&.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#007bff",
    },
  },
  inlineActions: {
    display: "flex",
    flexDirection: "column",
    marginLeft: "8px",
    gap: "4px",
  },
  iconBtn: {
    padding: "1px",
  },
  noteText: {
    marginTop: theme.spacing(2),
    fontSize: "14px",
    color: "#333",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    marginTop: theme.spacing(4),
  },
  backButton: {
    backgroundColor: "#555",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#333",
    },
  },
  saveButton: {
    backgroundColor: "#f26522",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#d4551a",
    },
  },
}));

const POStatusTab = (props) => {
  const { featuresAccess, sponsorId } = props;
  const classes = useStyles();

  const [poStatuses, setPoStatuses] = useState([]); // State to store PO statuses
  const [loading, setLoading] = useState(false); // State to manage loading spinner
  const [error, setError] = useState("");
  const [isUpdated, setIsUpdated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [savingStates, setSavingStates] = useState({});
  
  // Get edit access from featuresAccess
  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  const fetchPoStatuses = async () => {
    setLoading(true); // Show loading spinner
    setError(""); // Reset error state
    try {
      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/fetchFGStatusesDirect/false`; // API endpoint
      const response = await axios.get(url);

      if (response.data?.status === true || response.data?.status === "true") {
        //console.log("Fetched PO Statuses:", response.data?.data);
        //console.log("response", response);
        // Map the API response to match the expected structure
        const mappedStatuses = response.data?.data?.poStatuses?.map(
          (status) => ({
            id: status.status_id,
            name: status.status_name,
            selected: status.status_name === "Created" || status.status_name === "New PO" ? true : status.activated,
            type: status.status_type,
            isNew: false,
            newStatusName: status.newStatusName,
            lastUpdatedBy: status.last_updated_by,
          })
        );

        setPoStatuses(mappedStatuses || []); // Update state with mapped statuses
      } else {
        console.error("Failed to fetch PO statuses:", response.data?.message);
        setError(response.data?.message || "Failed to fetch PO statuses.");
      }
    } catch (error) {
      console.error("Error fetching PO statuses:", error);
      setError("An error occurred while fetching PO statuses.");
    } finally {
      setLoading(false); // Hide loading spinner
    }
  };

  useEffect(() => {
    fetchPoStatuses();
  }, []);

  const saveNewPo = async (index) => {
    const newStatus = poStatuses[index];
    if (!newStatus.name?.trim() || savingStates[index]) return;
    setSavingStates((prev) => ({ ...prev, [index]: true }));
    try {
      const newStatus = poStatuses[index]; // Get the new status to save
      if (!newStatus.name.trim()) {
        setError("Please provide a name for the new PO status.");
        return;
      }

      const payload = {
        sponsorId: sponsorId,
        ecosystemId: localStorage.getItem("ecosystemId"),
        saslBatchStatusesList: [
          {
            //status_id: newStatus.id, // Use the ID from the new status
            status_name: newStatus.name, // Use the name from the new status
            // Use the type from the new status
            status_type: "New",
            object_type: "PO",
            markForDelete: false,
            activated: newStatus.selected,
          },
        ],
      };

      //console.log(payload, "Payload for saving new PO status"); // Debugging

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/addSASLBatchStatuses`; // API endpoint
      const response = await axios.post(url, payload);


      if (response.data?.status === true || response.data?.status === "true") {
        //console.log("New PO status saved successfully:", response.data);
        fetchPoStatuses();
        props.setPoStatus((prev) => !prev);
        // Update the state to mark the status as saved
        setPoStatuses((prev) =>
          prev.map((status, i) =>
            i === index
              ? { ...status, id: response.data?.data?.id, isNew: false }
              : status
          )
        );
      } else {
        console.error("Failed to save new PO status:", response.data?.message);
        setError(response.data?.message || "Failed to save new PO status.");
      }
    } catch (error) {
      console.error("Error saving new PO status:", error);
      setError("An error occurred while saving the new PO status.");
    }
    setSavingStates((prev) => ({ ...prev, [index]: false }));
  };

  const handleDelete = async (index) => {
    try {
      const statusToDelete = poStatuses[index]; // Get the status to delete
      if (statusToDelete.isNew) {
        setPoStatuses((prev) => prev.filter((_, i) => i !== index));
        //console.log("New PO status removed without API call.");
        return;
      }

      // Prepare the payload for the API
      const payload = {
        ecosystemId: localStorage.getItem("ecosystemId"),
        sponsorId: sponsorId,
        poStatuses: [
          {
            status_id: statusToDelete.id, // ID of the status
            status_name: statusToDelete.name, // Name of the status
            activated: statusToDelete.selected, // Current activation state
            markForDelete: true, // Mark the status for deletion
            object_type: "PO",
            newStatusName: statusToDelete.newStatusName, // No new name for deletion
            status_type: statusToDelete.type,
          },
        ],
        batchStatuses: [],
        shipmentStatuses: [],
      };

      //console.log(payload, "Payload for deleting PO status"); // Debugging

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateFGStatuses`; // API endpoint
      const response = await axios.post(url, payload);
      fetchPoStatuses();
      if (response.data?.status === true || response.data?.status === "true") {
        //console.log("PO status deleted successfully:", response.data);
        props.setPoStatus((prev) => !prev);
        // Update the state to remove the deleted status
        setPoStatuses((prev) => prev.filter((_, i) => i !== index));
      } else {
        console.error("Failed to delete PO status:", response.data?.message);
        setError(response.data?.message || "Failed to delete PO status.");
      }
    } catch (error) {
      console.error("Error deleting PO status:", error);
      setError("An error occurred while deleting the PO status.");
    }
  };

  // const handleNextClick = async () => {
  //   try {
  //     // Validate that at least one PO status is selected

  //     const isAnyStatusSelected = poStatuses.filter(
  //       (status) => status.isUpdated
  //     );
  //     if (!isAnyStatusSelected.length) {
  //       console.log("No updates detected. Proceeding to the next step.");
  //       props.handleNext(); // Directly move to the next tab
  //       return;
  //     }
  //     // if (!isAnyStatusSelected.length) {
  //     //   setErrorMessage("Please activate at least one PO status.");
  //     //   return;
  //     // }
  //     if (poStatuses.every((status) => !status.selected)) {
  //       setErrorMessage("Please enable at least one in toggle mode.");
  //       return;
  //     }

  //     // Prepare the payload for the API
  //     const payload = {
  //       sponsorId: 2, // Example sponsorId
  //       ecosystemId: 1, // Example ecosystemId
  //       poStatuses: isAnyStatusSelected.map((status) => ({
  //         status_id: status.id, // ID of the PO status
  //         status_name: status.name, // Name of the PO status
  //         activated: status.selected, // Activation state
  //         object_type: "PO",
  //         // markForDelete: status.markForDelete || false, // Mark for deletion if applicable
  //         newStatusName: status.newName, // New name if updated
  //         status_type: status.type,
  //         markForDelete: false,
  //       })),
  //       batchStatuses: [],
  //       shipmentStatuses: [],
  //     };

  //     console.log(payload, "Payload for updating PO statuses"); // Debugging

  //     const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateFGStatuses`; // API endpoint
  //     const response = await axios.post(url, payload);
  //     fetchPoStatuses();
  //     if (response.data?.status === true || response.data?.status === "true") {
  //       console.log("PO statuses updated successfully:", response.data);

  //       // Move to the next tab
  //       props.handleNext();
  //     } else {
  //       console.error("Failed to update PO statuses:", response.data?.message);
  //       setError(response.data?.message || "Failed to update PO statuses.");
  //     }
  //   } catch (error) {
  //     console.error("Error updating PO statuses:", error);
  //     setError("An error occurred while updating PO statuses.");
  //   }
  // };
  const handleSaveChanges = async () => {
    try {
      // Validate that at least one PO status is selected

      const isAnyStatusSelected = poStatuses.filter(
        (status) => status.isUpdated
      );
      if (!isAnyStatusSelected.length) {
       // console.log("No updates detected. Proceeding to the next step.");

        return;
      }
      // if (!isAnyStatusSelected.length) {
      //   setErrorMessage("Please activate at least one PO status.");
      //   return;
      // }
      if (poStatuses.every((status) => !status.selected)) {
        setErrorMessage("Please enable at least one in toggle mode.");
        return;
      }

      // Prepare the payload for the API
      const payload = {
        sponsorId: sponsorId,
        ecosystemId: localStorage.getItem("ecosystemId"), // Example ecosystemId
        poStatuses: isAnyStatusSelected.map((status) => ({
          status_id: status.id, // ID of the PO status
          status_name: status.name, // Name of the PO status
          activated: status.selected, // Activation state
          object_type: "PO",
          // markForDelete: status.markForDelete || false, // Mark for deletion if applicable
          newStatusName: status.newStatusName, // New name if updated
          status_type: status.type,
          markForDelete: false,
        })),
        batchStatuses: [],
        shipmentStatuses: [],
      };

      //console.log(payload, "Payload for updating PO statuses"); // Debugging

      const url = `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}/updateFGStatuses`; // API endpoint
      const response = await axios.post(url, payload);
      fetchPoStatuses();
      if (response.data?.status === true || response.data?.status === "true") {
        //console.log("PO statuses updated successfully:", response.data);

        // Move to the next tab
      } else {
        console.error("Failed to update PO statuses:", response.data?.message);
        setError(response.data?.message || "Failed to update PO statuses.");
      }
    } catch (error) {
      console.error("Error updating PO statuses:", error);
      setError("An error occurred while updating PO statuses.");
    }
  };
  // const handleToggle = (index) => {
  //   const updated = [...poStatuses];
  //   updated[index].selected = !updated[index].selected;
  //   updated[index].isUpdated = true;
  //   setPoStatuses(updated);
  //   setIsUpdated(true);
  // };

  //   const handleToggle = (index) => {
  //   const updated = [...poStatuses];
  //   const toggledStatus = updated[index];

  //   // Change this condition as needed for your new toggle logic
  //   if (toggledStatus.name === "New PO") {
  //     if (!toggledStatus.selected) {
  //       // Turn ON "New PO Logged on", OFF the other two
  //       updated.forEach((status) => {
  //         if (status.name === "New PO") {
  //           status.selected = true;
  //           status.isUpdated = true;
  //         } else if (
  //           status.name === "Created" ||
  //           status.name === "Shared with CMO"
  //         ) {
  //           status.selected = false;
  //           status.isUpdated = true;
  //         }
  //       });
  //     } else {
  //       // Turn OFF "New PO Logged on", ON the other two
  //       updated.forEach((status) => {
  //         if (status.name === "New PO") {
  //           status.selected = false;
  //           status.isUpdated = true;
  //         } else if (
  //           status.name === "Created" ||
  //           status.name === "Shared with CMO"
  //         ) {
  //           status.selected = true;
  //           status.isUpdated = true;
  //         }
  //       });
  //     }
  //     setPoStatuses(updated);
  //     setIsUpdated(true);
  //     return;
  //   }

  //   // If toggling "Created" or "Shared with CMO"
  //   if (
  //     toggledStatus.name === "Created" ||
  //     toggledStatus.name === "Shared with CMO"
  //   ) {
  //     toggledStatus.selected = !toggledStatus.selected;
  //     toggledStatus.isUpdated = true;
  //     if (toggledStatus.selected) {
  //       // Turn OFF "New PO Logged on"
  //       updated.forEach((status) => {
  //         if (status.name === "New PO") {
  //           status.selected = false;
  //           status.isUpdated = true;
  //         }
  //       });
  //     }
  //     setPoStatuses(updated);
  //     setIsUpdated(true);
  //     return;
  //   }

  //   // Default toggle
  //   toggledStatus.selected = !toggledStatus.selected;
  //   toggledStatus.isUpdated = true;
  //   setPoStatuses(updated);
  //   setIsUpdated(true);
  // };
  const handleToggle = (index) => {
    const updated = [...poStatuses];
    const toggledStatus = updated[index];

    // Prevent toggling for 'Created' and 'New PO' statuses
    if (toggledStatus.name === "Created" || toggledStatus.name === "New PO") {
      return; // Do nothing - these statuses should always be on
    }

    if (toggledStatus.name === "Shared with CMO") {
      const newValue = !toggledStatus.selected;
      updated.forEach((status) => {
        if (status.name === "Shared with CMO") {
          status.selected = newValue;
          status.isUpdated = true;
        }
      });
      setPoStatuses(updated);
      setIsUpdated(true);
      return;
    }

    // Default toggle for any other status
    toggledStatus.selected = !toggledStatus.selected;
    toggledStatus.isUpdated = true;
    setPoStatuses(updated);
    setIsUpdated(true);
  };

  const handleNameChange = (index, value) => {
    const updated = [...poStatuses];
    updated[index].name = value;
    setPoStatuses(updated);
    setIsUpdated(true);
  };

  // const handleDelete = (index) => {
  //   const updated = poStatuses.filter((_, i) => i !== index);
  //   setPoStatuses(updated);
  // };

  const handleAddStatus = () => {
    //const nextId = (poStatuses.length + 1).toString().padStart(2, "0");
    setPoStatuses([
      ...poStatuses,
      {
        // id: nextId,
        name: "",
        selected: false,
        isNew: true,
      },
    ]);
  };
  const handleResetChanges = () => {
    if (isUpdated) {
      fetchPoStatuses(); // Re-fetch the product types from the backend
      setIsUpdated(false); // Reset the update state
      setErrorMessage(""); // Clear any error messages
    }
  };
  // const saveNewPo = () => {
  //   console.log("Save new UOM action triggered");
  //   // Add logic to save the new UOM
  // };

  // Define handleCancel function
  // const handleCancel = () => {
  //   console.log("Cancel action triggered");
  //   // Add logic to handle cancel action
  // };
  const handleCancel = (index) => {
    setPoStatuses((prev) => prev.filter((_, i) => i !== index));
    //console.log(`PO status at index ${index} has been removed.`);
  };
  // ...existing code...



  return (
    <div
      className={classes.container}
      style={{ display: "flex", flexDirection: "column", gap: "24px" }}
    >
      {/* Table and Buttons Section */}
      <div style={{ display: "flex", gap: "40px", alignItems: "flex-start" }}>
        <TableContainer component={Paper} className={`${classes.table} default-table-styles`}>
          <Table className={`${classes.table} default-table-styles`}>
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Selection</TableCell>
                <TableCell className={classes.headerCell}>PO Status</TableCell>
                <TableCell className={classes.headerCell}>
                  Renamed PO Status
                </TableCell>
                <TableCell className={classes.headerCell}>
                  PO Status ID
                </TableCell>
                <TableCell className={classes.headerCell}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100px",
                      }}
                    >
                      <CircularProgress />
                    </div>
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "60px",
                        backgroundColor: "#ffecec",
                        color: "#b00020",
                        border: "1px solid #ff9999",
                      }}
                    >
                      <Typography>{error}</Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : poStatuses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "60px",
                        color: "#666",
                      }}
                    >
                      <Typography>No PO Statuses Found</Typography>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                poStatuses.map((status, index) => (
                  <TableRow key={status.id || index}>
                    <TableCell className={classes.bodyCell}>
                      <Switch
                        checked={status.name === "Created" || status.name === "New PO" ? true : status.selected}
                        onChange={() => handleToggle(index)}
                        disabled={!canEdit || status.name === "Created" || status.name === "New PO"}
                        classes={{
                          switchBase: classes.switchBase,
                        }}
                      />
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {status.isNew ? (
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <TextField
                            className={classes.textField}
                            placeholder="Type Text Here"
                            value={status.name}
                            onChange={(e) =>
                              handleNameChange(index, e.target.value)
                            }
                            onBlur={(e) => {
                              const { value } = e.target;
                              // Trim leading and trailing spaces for string values
                              if (typeof value === "string" && value.trim() !== value) {
                                handleNameChange(index, value.trim());
                              }
                            }}
                            variant="outlined"
                            inputProps={{
                              onBeforeInput: (e) => {
                                const allowedChars = /^[a-zA-Z0-9\s\-()]+$/;
                                if (!allowedChars.test(e.data)) {
                                  e.preventDefault();
                                }
                              }
                            }}
                          />
                          <div className={classes.inlineActions}>
                            <IconButton
                              onClick={() => handleCancel(index)}
                              className={classes.iconBtn}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={() => saveNewPo(index)}
                              className={classes.iconBtn}
                              disabled={
                                !status.name?.trim() ||
                                poStatuses.some(
                                  (s, i) =>
                                    i !== index &&
                                    s.name?.trim().toLowerCase() === status.name?.trim().toLowerCase()
                                ) || savingStates[index]
                              }
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </div>
                      ) : (
                        status.name
                      )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {editingIndex === index ? (
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <TextField
                            value={renameValue}
                            onChange={e => setRenameValue(e.target.value)}
                            onBlur={(e) => {
                              const { value } = e.target;
                              // Trim leading and trailing spaces for string values
                              if (typeof value === "string" && value.trim() !== value) {
                                setRenameValue(value.trim());
                              }
                            }}
                            autoFocus
                            size="small"
                            variant="outlined"
                            placeholder="Rename PO Status"
                            className={classes.textField}
                            style={{ marginRight: 8 }}
                            inputProps={{
                              onBeforeInput: (e) => {
                                const allowedChars = /^[a-zA-Z0-9\s\-()]+$/;
                                if (!allowedChars.test(e.data)) {
                                  e.preventDefault();
                                }
                              }
                            }}
                          />
                          <div className={classes.inlineActions}>
                            <IconButton
                              onClick={() => setEditingIndex(null)}
                              className={classes.iconBtn}
                            >
                              <CancelIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              onClick={async () => {
                                const updated = [...poStatuses];
                                updated[index].newStatusName = renameValue;
                                updated[index].isUpdated = true;
                                setPoStatuses(updated);
                                setEditingIndex(null);
                                await handleSaveChanges(); // <-- This will call your API
                              }}
                              className={classes.iconBtn}
                            >
                              <SaveIcon fontSize="small" />
                            </IconButton>
                          </div>
                        </div>
                      ) : (
                        <span style={{ display: "flex", alignItems: "center" }}>
                          {status.newStatusName || ""}
                          {canEdit && status.name !== "Created" && status.name !== "New PO" && (
                            <IconButton
                              size="small"
                              onClick={() => {
                                setEditingIndex(index);
                                setRenameValue(status.newStatusName || "");
                              }}
                              style={{ marginLeft: 4 }}
                            >
                              <EditIcon style={{ color: "orange" }} />
                            </IconButton>
                          )}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {status.id}
                    </TableCell>
                    <TableCell className={classes.bodyCell}>
                      {canEdit && (status.isNew === true ||
                        (status.type === "New" && status.lastUpdatedBy !== "support@schrocken.com")) &&
                        status.name !== "Created" && status.name !== "New PO" && (
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(index)}
                            className={classes.deleteIcon}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <div
          className={classes.buttonContainer}
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "16px", // Adjusted gap between buttons
            marginTop: "3px",
            marginLeft: "0rem", // Added margin to create space between table and buttons
          }}
        >
          <Button
            className={classes.saveButton}
            type="button"
            onClick={handleSaveChanges}
            disabled={!isUpdated || !canEdit}
            style={{

              opacity: !isUpdated || !canEdit ? 0.7 : 1, // Fade out when disabled
              color: "#fff",
            }}
          >
            Save Selection
          </Button>

          <Button
            className={classes.resetChangesButton}
            type="button"
            onClick={handleResetChanges}
            disabled={!isUpdated || !canEdit} // or whatever your "no changes" condition is
            style={{
              opacity: !isUpdated || !canEdit ? 0.8 : 1, // Fade out when disabled
            }}
          >
            Reset Selection
          </Button>
        </div>
      </div>

      {errorMessage && (
        <div style={{ color: "red", marginTop: 10 }} className="error-message">
          {errorMessage}
        </div>
      )}
      {/* <Typography className={classes.noteText}>
        Note - If New PO Status is selected, Created and Shared with CMO
        statuses cannot be selected and vice versa.
      </Typography> */}

      {canEdit && (
        <Button
          variant="outlined"
          onClick={handleAddStatus}
          className={classes.addButton}
        >
          <span className={classes.addIconCircle}>+</span>
          Add New PO Status
        </Button>
      )}

      {/* <div className={classes.footer}>
        <Button
          className={classes.backButton}
          onClick={() => {
            props.handleBack();
          }}
        >
          &lt; Back
        </Button>
        <StyledButton
          className={classes.saveButton}
          // onClick={() => {
          //   if (isUpdated) {
          //     handleNextClick(); // Call API and move to the next tab if updates are made
          //   } else {
          //     props.handleNext(); // Directly move to the next tab if no updates are made
          //   }
          // }}
          onClick={() => {
            handleNextClick(); // Call API and move to the next tab if updates are made
          }}
        >
          Save & Next
        </StyledButton>
      </div> */}
    </div>
  );
};

export default POStatusTab;
